import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import anime from 'animejs';
import { LazyImage } from 'project/lazy-image';
import {
    getPlayerExternalLink,
    livePlayer,
} from 'components/live-player';
import ScoringLoader, { endpointNames } from 'general/js/loaders/scoring-loader';
import { getHoleExternalLink, liveHole } from 'components/live-hole';

class LiveStatsRow extends React.Component {
    constructor(props) {
        super(props);

        this.rootRef = React.createRef();
        this.valueFillRef = React.createRef();
        this.valueTitleRef = React.createRef();
    }

    animate() {
        if (this.rootRef.current) {
            this.rootRef.current.classList.remove('has-transition');
        }

        anime({
            targets: this.valueFillRef.current,
            width: [0, `${this.getProgressWidth()}%`],
            duration: 600,
            easing: 'easeOutQuad',
            complete: this.addTransitionCss
        });

        anime({
            targets: this.valueTitleRef.current,
            opacity: [0, 1],
            duration: 800,
            easing: 'linear',
        });
    }

    componentDidMount() {
        ScoringLoader.subscribe(endpointNames.COURSE);
    }

    componentWillUnmount() {
        ScoringLoader.unsubscribe(endpointNames.COURSE);
    }

    getProgressWidth() {
        const { model, options } = this.props;
        let value = typeof model.value === 'string' ? model.value.replace('%', '') : model.value;
        let width = 0;

        if (value < options.min) {
            value = options.min;
        }

        if (value > options.max) {
            value = options.max;
        }

        if (options.isProgress) {
            width = 100 / (options.min + options.max) * value;
        }

        if (options.isCentered) {
            if (value < 0) {
                width = 100 / options.min * value;
            } else {
                width = 100 / options.max * value;
            }
        }

        return width;
    }

    addTransitionCss = () => {
        if (this.rootRef.current) {
            this.rootRef.current.classList.add('has-transition');
        }
    }

    onClickPlayer = (event, id) => {
        if (this.props.targetsConfig) return;

        livePlayer.show(id);
        event.preventDefault();
    }

    onClickHole = (event, id) => {
        if (this.props.targetsConfig) return;

        liveHole.show(id);
        event.preventDefault();
    }

    renderPlayerInfo() {
        const { model, targetsConfig } = this.props;
        const playerSD = model.sitecoreData;
        let playerUrl = null;

        if (targetsConfig) {
            playerUrl = getPlayerExternalLink(targetsConfig.url, model.playerId);
        }

        return (
            <div className="live-stats-row__info live-stats-row__info--player">
                <a
                    className="live-stats-row__photo of-contain"
                    href={playerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => this.onClickPlayer(event, model.playerId)}
                >
                    <LazyImage image={playerSD.cutoutSrc} />
                </a>
                <a
                    className="live-stats-row__prop"
                    href={playerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => this.onClickPlayer(event, model.playerId)}
                    aria-label={`Show ${playerSD.firstName} ${playerSD.lastName} card`}
                >
                    <span className="live-stats-row__prop-title">{playerSD.firstName}</span>
                    <span className="live-stats-row__prop-subtitle">{playerSD.lastName}</span>
                </a>
            </div>
        );
    }

    renderHoleInfo() {
        const { model, targetsConfig } = this.props;
        const stateCss = classNames({
            'not-interactive': !this.props.isInteractive
        });
        let holeUrl = null;

        if (targetsConfig) {
            holeUrl = getHoleExternalLink(targetsConfig.url, model.holeId);
        }

        return (
            <div className="live-stats-row__info">
                <a
                    className={`live-stats-row__hole-flag ${stateCss} hole-flag hole-flag--base`}
                    href={holeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => this.onClickHole(event, model.holeId)}>{model.holeId}</a>
            </div>
        );
    }

    renderSingleInfo() {
        return (
            <div className="live-stats-row__info">
                <div className="live-stats-row__text">{this.props.model.text}</div>
            </div>
        );
    }

    render() {
        const {
            model, options, hasAnimation, scoringCourse
        } = this.props;

        if (!model) return null;

        let activeHole = null;
        if (model.holeId && scoringCourse) activeHole = scoringCourse.holes.find((hole) => hole.id === model.holeId);
        const stateCss = classNames({
            'is-progress': options.isProgress,
            'is-reversed': options.isReversed,
            'is-centered': options.isCentered,
            'is-negative': model.value < 0,
            'is-single': model.isSingle,
            'is-player': model.isPlayer,
            'has-transition': !hasAnimation,
        });
        const value = model.displayValue || model.value;
        if (value % 1 != 0) {
            var modifiedValue = value.toFixed(2);
        } else {
            modifiedValue = value;
        }
        return (
            <div className={`live-stats-row ${stateCss}`} ref={this.rootRef}>
                {model.playerId && !model.isSingle && this.renderPlayerInfo()}
                {model.holeId && this.renderHoleInfo()}
                {model.isSingle && this.renderSingleInfo()}
                <div className="live-stats-row__value">
                    <div
                        className="live-stats-row__value-fill"
                        style={{ width: `${this.getProgressWidth()}%` }}
                        ref={this.valueFillRef}
                    >

                    </div>

                </div>
                <div className="live-stats-row__value-title" ref={this.valueTitleRef}>
                    <div>
                        <span>{modifiedValue}</span>
                        {
                            options.showPercentage &&
                        <span className="live-stats-row__value-subtitle"></span>
                        }
                    </div>
                    {options.easiestOrHardest && scoringCourse && <span className="live-stats-row__value-par">PAR {activeHole.par}</span>}
                </div>
            </div>
        );
    }
}

LiveStatsRow.defaultProps = {
    options: {
        isProgress: false,
        isCentered: false,
        isReverse: false,
        showPercentage: false
    },
    shouldAnimated: false
};

LiveStatsRow.propTypes = {
    model: PropTypes.object,
    options: PropTypes.object,
    targetsConfig: PropTypes.shape({
        url: PropTypes.string,
        allResultsUrl: PropTypes.string,
    }),
    isInteractive: PropTypes.bool,
};

const mapStateToProps = (state) => ({
    scoringCourse: state.scoring ? state.scoring[endpointNames.COURSE] : undefined
});
const LiveStatsRowContainer = connect(
    mapStateToProps
)(LiveStatsRow);
export default LiveStatsRowContainer;
